import React, { useContext, useEffect, useRef, useState } from 'react';
import { Box, TextField, Typography } from '@mui/material';
import {
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  ArrowBack as ArrowBackIcon,
} from '@mui/icons-material/';
import PersonIcon from '@mui/icons-material/Person';
import SavingsIcon from '@mui/icons-material/Savings';
import RemoveCircleIcon from '@mui/icons-material/RemoveCircle';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import StorefrontIcon from '@mui/icons-material/Storefront';
import Inventory2Icon from '@mui/icons-material/Inventory2';
import ConfirmationNumberIcon from '@mui/icons-material/ConfirmationNumber';
import PhotoCameraIcon from '@mui/icons-material/PhotoCamera';
import ModalKioskProducts from './ModalKioskProducts';
import ModalKioskStock from './ModalKioskStock';
import ModalNtLoddStock from './ModalNtLoddStock';
import ReceiptCapture from '../../../components/ReceiptCapture';
import toast, { Toaster } from 'react-hot-toast';

import { AppContext } from '../../../context/Context';
import { SocketContext } from '../../../context/SocketContext';
import { useTranslation } from 'react-i18next';
import '../../../assets/css/buttons.css';

import CashField from '../../AvslutteSkiftet/CashField';
import { formatSignedCash } from '../../../utils/formatCash';
import {
  verticallyCenteredLabelSx,
  fieldHelpSx,
} from '../../../theme/fieldTokens';

// One door for every hand-entered cash movement: Refusjon to a player, the other two to the drawer
function ModalCashMovement() {
  const { t } = useTranslation();
  const { socket } = useContext(SocketContext);

  const {
    hall,
    operator,
    session,
    currentUser,
    updateShowModalCashMovement,
    updateShowModalRefund,
    updateShowModalSafe,
    updateIsLoadingScreen,
    updateFocusOnMainScreen,
  } = useContext(AppContext);

  // null → choosing; 'out' | 'in' → the till-movement form; 'kiosk' → read-only
  // list; 'stock' and 'lodd' → the two goods screens
  const [kind, setKind] = useState(null);
  const [amount, setAmount] = useState('');
  const [reason, setReason] = useState('');
  // Receipts for a withdrawal: optional, and several because a long receipt
  // does not fit in one shot. Only 'out' collects them — 'in' has the deposit
  // itself as its evidence
  const [photoUrls, setPhotoUrls] = useState([]);
  const [capturing, setCapturing] = useState(false);
  const [miniSafe, setMiniSafe] = useState(null);
  const [safe, setSafe] = useState(null);
  const isBusy = useRef(false);

  //* DISABLE FOCUS ON MAIN SCREEN AND PREVENT ESCAPE BUTTON
  useEffect(() => {
    updateFocusOnMainScreen(false);

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        event.preventDefault();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      // Restored here rather than on each exit path, so no route out forgets
      updateFocusOnMainScreen(true);
    };
  }, []);

  // The landing screen doubles as the cash overview, so it asks on open
  useEffect(() => {
    if (!hall) return;
    socket.emit('miniSafeState', { bingoName: hall });
    socket.emit('safeState', { bingoName: hall });
  }, [hall]);

  useEffect(() => {
    const handleTillMovement = (data) => {
      updateIsLoadingScreen(false);
      isBusy.current = false;

      if (!data) {
        toast.error(t('error.error'));
        return;
      }
      if (data.ok === false) {
        toast.error(
          data.reason === 'amountRequired'
            ? t('sales.cashMovementAmountRequired')
            : t('sales.cashMovementReasonRequired')
        );
        return;
      }

      toast.success(t('sales.cashMovementDone'));
      updateShowModalCashMovement(false);
    };

    const handleMini = (data) => data && setMiniSafe(data);
    const handleSafe = (data) => data && setSafe(data);

    socket.on('tillMovement', handleTillMovement);
    socket.on('miniSafeState', handleMini);
    socket.on('safeState', handleSafe);

    return () => {
      socket.off('tillMovement', handleTillMovement);
      socket.off('miniSafeState', handleMini);
      socket.off('safeState', handleSafe);
    };
  }, [socket]);

  const handleChooseSafe = () => {
    updateShowModalCashMovement(false);
    updateShowModalSafe(true);
  };

  const handleChooseRefund = () => {
    // Refusjon is tied to a player; the other two are not
    if (!currentUser) {
      toast.error(t('sales.cashMovementNoPlayer'));
      return;
    }
    updateShowModalCashMovement(false);
    updateShowModalRefund(true);
  };

  const handleConfirm = () => {
    if (amount === '' || Number(amount) <= 0) {
      toast.error(t('sales.cashMovementAmountRequired'));
      return;
    }
    if (!reason.trim()) {
      toast.error(t('sales.cashMovementReasonRequired'));
      return;
    }
    if (isBusy.current) return;

    isBusy.current = true;
    updateIsLoadingScreen(true);
    socket.emit('tillMovement', {
      kind,
      bingoName: hall,
      operator: operator,
      session: session,
      amount: Number(amount),
      reason: reason.trim(),
      photoUrls,
    });
  };

  const choiceTile = (icon, label, onClick, tone, wide = false) => (
    <button
      className={`btn-choice tone-${tone}${wide ? ' choice-wide' : ''}`}
      onClick={onClick}
    >
      {icon}
      {label}
    </button>
  );

  return (
    <div id="react-confirm-alert">
      <div className="react-confirm-alert-overlay">
        <div className="react-confirm-alert">
          <div
            className="react-confirm-alert-body modal-status"
            style={{ width: '760px' }}
          >
            {/* Mounted inside the overlay, not outside it: the Toasters further up
                the tree sit in their own stacking contexts, so their toasts paint
                behind this modal however high their z-index goes. One here covers
                every screen this modal opens */}
            <Toaster
              position="top-center"
              reverseOrder={false}
              toastOptions={{ style: { fontSize: '24px' } }}
              containerStyle={{ zIndex: 99999999 }}
            />

            {kind === null ? (
              <>
                <h1>{t('sales.cashMovementTitle')}</h1>
                <p>{t('sales.cashMovementSubtitle')}</p>

                {/* One door for cash means this screen can also be where you
                    look, not only where you act */}
                <Box
                  sx={{
                    display: 'flex',
                    gap: 2,
                    mt: 2,
                    mb: 1,
                  }}
                >
                  {[
                    [
                      t('safe.miniSafe'),
                      miniSafe?.balance,
                      miniSafe && !miniSafe.countedToday
                        ? t('safe.notCountedToday')
                        : null,
                    ],
                    [
                      t('safe.safe'),
                      safe?.total,
                      safe ? t('safe.bagCount', { count: safe.count }) : null,
                    ],
                  ].map(([label, value, note]) => (
                    <Box
                      key={label}
                      sx={{
                        flex: 1,
                        px: 2,
                        py: 1.5,
                        borderRadius: '14px',
                        backgroundColor: '#151d3b',
                        border: '1px solid #2a3a6e',
                      }}
                    >
                      <Typography sx={{ color: '#8fa3d4', fontSize: '15px' }}>
                        {label}
                      </Typography>
                      <Typography
                        sx={{
                          fontSize: '28px',
                          fontWeight: 700,
                          color: '#e6eaf2',
                          fontVariantNumeric: 'tabular-nums',
                        }}
                      >
                        {value == null ? '—' : `${formatSignedCash(value)} kr`}
                      </Typography>
                      {note && (
                        <Typography sx={{ color: '#fcad89', fontSize: '14px' }}>
                          {note}
                        </Typography>
                      )}
                    </Box>
                  ))}
                </Box>

                <div className="choice-grid">
                  {choiceTile(
                    <PersonIcon />,
                    t('sales.cashMovementRefund'),
                    handleChooseRefund,
                    'refund'
                  )}
                  {choiceTile(
                    <RemoveCircleIcon />,
                    t('sales.cashMovementOut'),
                    () => setKind('out'),
                    'out'
                  )}
                  {choiceTile(
                    <AddCircleIcon />,
                    t('sales.cashMovementIn'),
                    () => setKind('in'),
                    'in'
                  )}
                  {choiceTile(
                    <SavingsIcon />,
                    t('sales.cashMovementSafe'),
                    handleChooseSafe,
                    'safe'
                  )}
                  {/* Reading, not moving: the one door for cash is also where
                      you check what the kiosk actually sold */}
                  {choiceTile(
                    <StorefrontIcon />,
                    t('sales.cashMovementKiosk'),
                    () => setKind('kiosk'),
                    'sales'
                  )}
                  {/* Goods, not money: what the hall has left of each product */}
                  {choiceTile(
                    <Inventory2Icon />,
                    t('sales.cashMovementStock'),
                    () => setKind('stock'),
                    'stock'
                  )}
                  {/* Norsk Tipping lodd: goods again, but with no sales feed
                      behind them */}
                  {choiceTile(
                    <ConfirmationNumberIcon />,
                    t('sales.cashMovementLodd'),
                    () => setKind('lodd'),
                    'lodd'
                  )}
                  {choiceTile(
                    <CancelIcon />,
                    t('sales.cancel'),
                    () => updateShowModalCashMovement(false),
                    'dismiss',
                    true
                  )}
                </div>
              </>
            ) : kind === 'kiosk' ? (
              <ModalKioskProducts onBack={() => setKind(null)} />
            ) : kind === 'stock' ? (
              <ModalKioskStock onBack={() => setKind(null)} />
            ) : kind === 'lodd' ? (
              <ModalNtLoddStock onBack={() => setKind(null)} />
            ) : capturing ? (
              /* Replaces the form rather than sitting under it: ReceiptCapture
                 owns the shared `uploadImageSignature` channel and must be
                 mounted only while the camera is meant to be on */
              <ReceiptCapture
                title={t('sales.cashMovementReceiptTitle')}
                help={t('sales.cashMovementReceiptHelp')}
                filePrefix="uttak_kasse"
                onDone={(url) => {
                  setPhotoUrls((urls) => [...urls, url]);
                  setCapturing(false);
                }}
                onCancel={() => setCapturing(false)}
              />
            ) : (
              <>
                <h1>
                  {kind === 'out'
                    ? t('sales.cashMovementOut')
                    : t('sales.cashMovementIn')}
                </h1>

                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'center',
                    marginTop: '20px',
                  }}
                >
                  <CashField
                    label={t('sales.cashMovementAmount') + '*'}
                    value={amount}
                    onChange={setAmount}
                    autoFocus
                  />
                </div>

                <TextField
                  autoComplete="off"
                  label={t('sales.cashMovementReason') + '*'}
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  variant="outlined"
                  multiline
                  maxRows={4}
                  inputProps={{ maxLength: 500 }}
                  InputLabelProps={{ sx: verticallyCenteredLabelSx }}
                  className="modal-textfield"
                  style={{ width: '100%', marginTop: '24px' }}
                />
                <Typography sx={fieldHelpSx}>
                  {t('sales.cashMovementReasonHelp')}
                </Typography>

                {/* Only on the way out: `Uttak kasse` is the one movement with
                    nothing but the typed reason standing behind it */}
                {kind === 'out' && (
                  <Box sx={{ mt: 3, textAlign: 'center' }}>
                    <button
                      className="btn-custom-camara"
                      onClick={() => setCapturing(true)}
                      style={{ marginTop: 0 }}
                    >
                      <PhotoCameraIcon style={{ fontSize: '1.2em' }} />
                      {photoUrls.length
                        ? t('sales.cashMovementReceiptMore')
                        : t('sales.cashMovementReceipt')}
                    </button>
                    <Typography sx={fieldHelpSx}>
                      {photoUrls.length
                        ? t('sales.cashMovementReceiptCount', {
                            count: photoUrls.length,
                          })
                        : t('sales.cashMovementReceiptHelp')}
                    </Typography>
                  </Box>
                )}

                <div className="react-confirm-alert-button-group">
                  <button
                    className="btn-custom-yes"
                    onClick={handleConfirm}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '8px',
                    }}
                  >
                    <CheckCircleIcon style={{ fontSize: '1.2em' }} />
                    {t('sales.confirm')}
                  </button>
                  <button
                    className="btn-custom-back"
                    onClick={() => {
                      setKind(null);
                      setAmount('');
                      setReason('');
                      setPhotoUrls([]);
                    }}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '8px',
                    }}
                  >
                    <ArrowBackIcon style={{ fontSize: '1.2em' }} />
                    {t('sales.openShiftBack')}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default ModalCashMovement;
